import React, { useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useTranslation } from '../i18n';
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/place/Dentico+Clinic+-+Private+Dentist+in+Angel,+Islington/@51.5308911,-0.1029003,18z/data=!4m8!3m7!1s0x48760f7c6534e077:0xa54a590bd3b2a149!8m2!3d51.5308911!4d-0.1029003!9m1!1b1!16s%2Fg%2F11vq9ndlvk?entry=ttu&g_ep=EgoyMDI2MDIwMS4wIKXMDSoASAFQAw%3D%3D';
const LEAVE_REVIEW_URL = 'https://g.page/r/CUmhstMLWUqlEBM/review';
export function GoogleReviews() {
  const {
    t
  } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  // 5-star reviews data (all are 5-star filtered)
  const allReviews = [{
    name: t('reviews.review1.name'),
    text: t('reviews.review1.text'),
    date: t('reviews.review1.date'),
    rating: 5
  }, {
    name: t('reviews.review2.name'),
    text: t('reviews.review2.text'),
    date: t('reviews.review2.date'),
    rating: 5
  }, {
    name: t('reviews.review3.name'),
    text: t('reviews.review3.text'),
    date: t('reviews.review3.date'),
    rating: 5
  }, {
    name: t('reviews.review4.name'),
    text: t('reviews.review4.text'),
    date: t('reviews.review4.date'),
    rating: 5
  }, {
    name: t('reviews.review5.name'),
    text: t('reviews.review5.text'),
    date: t('reviews.review5.date'),
    rating: 5
  }, {
    name: t('reviews.review6.name'),
    text: t('reviews.review6.text'),
    date: t('reviews.review6.date'),
    rating: 5
  }];
  // Show max 6 reviews, with option to load more
  const displayedReviews = showAll ? allReviews : allReviews.slice(0, 6);
  // Get reviewer initials (first name + last initial)
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1].charAt(0)}.`;
    }
    return name;
  };
  if (allReviews.length === 0) {
    return null; // Hide section if no reviews
  }
  return <section className="py-20 lg:py-28 bg-white" data-id="element-355" data-mp="3tj162fu3n193">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-356" data-mp="3th7y8x7r2azi">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-357" data-mp="3phz4hscpmd3y">
          <p className="text-sm font-medium text-forest-600 uppercase tracking-wide mb-3" data-id="element-358" data-mp="3petca48sav40">
            {t('reviews.title')}
          </p>
          <h2 className="text-4xl md:text-5xl font-medium text-forest-900 mb-4" data-id="element-359" data-mp="l39503c5x3pp">
            {t('reviews.subtitle')}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-360" data-mp="h1vz5t92l0is">
            <div className="flex gap-1" data-id="element-361" data-mp="c187r6gxiho1">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-6 h-6 text-yellow-400 fill-current" data-id="element-362" data-mp="73zlatoqzrgz" />)}
            </div>
          </div>
          <p className="text-sm text-stone-500" data-id="element-363" data-mp="2rtp39fkqztdw">{t('reviews.disclaimer')}</p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-12" data-id="element-364" data-mp="2hqjp8gh5l9yk">
          {displayedReviews.map((review, idx) => <div key={idx} className="bg-ivory border border-stone-200 rounded-2xl p-8 h-full flex flex-col" data-id="element-365" data-mp="2mov2702qw0l0" data-mpi={idx}>
              {/* 5 Stars */}
              <div className="flex items-center gap-1 mb-4" data-id="element-366" data-mp="2wfxzdqsjzvf0">
                {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" data-id="element-367" data-mp="30fqd0hwt0rr8" />)}
              </div>

              {/* Review Text */}
              <p className="text-stone-700 mb-6 flex-grow leading-relaxed" data-id="element-368" data-mp="3acbif4cqk4vl">
                "{review.text}"
              </p>

              {/* Reviewer Info */}
              <div className="flex justify-between items-end mt-auto pt-4 border-t border-stone-200" data-id="element-369" data-mp="1g1g7hve2wdpn">
                <span className="font-medium text-forest-900" data-id="element-370" data-mp="1kzrjocruj0b1">
                  {getInitials(review.name)}
                </span>
                <span className="text-sm text-stone-500" data-id="element-371" data-mp="3bi67869yrigo">{review.date}</span>
              </div>
            </div>)}
        </div>

        {/* Load More Button (if more than 6 reviews) */}
        {allReviews.length > 6 && !showAll && <div className="text-center mb-10" data-id="element-372" data-mp="3fizvx1v8d1am">
            <Button variant="outline" onClick={() => setShowAll(true)} data-id="element-373" data-mp="3ffu95ta2qbi2">
              {t('reviews.viewAllButton')}
            </Button>
          </div>}

        {/* CTA Buttons */}
        <div className="text-center flex flex-col sm:flex-row gap-4 justify-center" data-id="element-374" data-mp="2nslkrdelq32n">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-id="element-375" data-mp="31jqdrwngrx40">
            <Button variant="outline" size="lg" className="gap-2" data-id="element-376" data-mp="2wpm6tde77p2x">
              <ExternalLink className="w-5 h-5" data-id="element-377" data-mp="290a2aqttg56y" />
              {t('reviews.viewAllButton')}
            </Button>
          </a>
          <a href={LEAVE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-id="element-378" data-mp="240xeo3v4eu3b">
            <Button size="lg" className="gap-2" data-id="element-379" data-mp="jjfwswptf1cd">
              <Star className="w-5 h-5" data-id="element-380" data-mp="obz9lrgohsg4" />
              {t('reviews.leaveReview')}
            </Button>
          </a>
        </div>
      </div>
    </section>;
}